import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui'
import { AuthContext } from '@/contexts/authContextDef'
import { useSupportSession } from '@/hooks/useSupportSession'
import { endSupportSession, type SupportSessionInfo } from '@/lib/supportSession'

// "7:41": hours and minutes left, never below 0:00.
function timeLeft(endsAt: string, now: number): string | null {
  const ends = Date.parse(endsAt)
  if (Number.isNaN(ends)) return null
  const minutes = Math.max(0, Math.floor((ends - now) / 60_000))
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
}

/**
 * EP-160 section 2.3, S3: the bar above every page of a FlowRent support tab.
 * "⚠ บัญชี W Living · ทีม FlowRent (Nok) · ขอบเขต: ตั้งค่าให้ฉัน · เหลือ 7:41 ชม.
 * · [จบเซสชัน]". Browser only: useSupportSession is never active on the
 * operator app, so the app never shows it.
 *
 * Z-ladder: z-30, the slot of the mobile page action bar and the sticky
 * Pagination bar, all under the drawer backdrop (z-40). MainLayout renders it
 * in flow above the sidebar and the page column of an h-screen shell, so it
 * takes its own height and pushes the page down instead of covering it; the
 * page scrolls inside <main> below it, and the fixed bottom action bar is
 * never under it.
 */
export function SupportSessionBanner() {
  const { active, info } = useSupportSession()
  if (!active) return null
  return <SupportSessionBar info={info} />
}

function SupportSessionBar({ info }: { info: SupportSessionInfo | null }) {
  const { t } = useTranslation('common')
  const accountName = useContext(AuthContext)?.user?.account.name
  const [now, setNow] = useState(() => Date.now())
  const [ending, setEnding] = useState(false)

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const left = info?.ends_at ? timeLeft(info.ends_at, now) : null
  const parts = [
    accountName ? t('support_session.banner.account', { name: accountName }) : null,
    info ? t('support_session.banner.team', { name: info.admin_name }) : null,
    info
      ? t('support_session.banner.scope', {
          label: t(info.scope_label_key ?? `support_session.scopes.${info.scope}`),
        })
      : null,
    left ? t('support_session.banner.time_left', { time: left }) : null,
  ].filter((part): part is string => part !== null)

  const end = async () => {
    setEnding(true)
    // The tab then shows "จบเซสชันแล้ว" (SupportSessionGate).
    await endSupportSession()
  }

  return (
    <div
      role="region"
      aria-label={t('support_session.banner.label')}
      data-testid="support-session-banner"
      className="sticky top-0 z-30 shrink-0 bg-warning text-warning-content px-4 py-2 text-sm"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
        {parts.map((part, index) => (
          <span key={part} className="whitespace-nowrap">
            {index > 0 && <span aria-hidden="true" className="mr-2">·</span>}
            {part}
          </span>
        ))}
        <Button
          size="sm"
          variant="outline"
          className="ml-auto border-warning-content text-warning-content"
          loading={ending}
          onClick={end}
          data-testid="support-session-end"
        >
          {t('support_session.banner.end')}
        </Button>
      </div>
    </div>
  )
}
